import { useState } from 'react';
import MarkdownEditor from '../shared/MarkdownEditor';
import { useTheme } from '../../hooks/useTheme';

export default function ItemDetailsSection({ item, onClose, onUpdate }) {
  const { currentThemeType, themeTypes } = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const isLightTheme = currentThemeType === themeTypes.LIGHT;

  if (!item) return null;

  const handleEdit = () => {
    setEditedContent(item.content || item.description || '');
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!onUpdate) {
      console.error('No onUpdate handler provided');
      return;
    }

    setIsSaving(true);
    try {
      await onUpdate(item.id, { content: editedContent });
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save item:', error);
      // Error is already handled by parent
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (isEditing) {
      setIsEditing(false);
      setEditedContent('');
    } else {
      onClose();
    }
  };

  return (
    <>
      <div className={`flex flex-col ${isLightTheme ? 'border-t border-gray-300 bg-gray-50' : 'border-t border-gray-700 bg-gray-800/30'}`} style={{ height: '400px' }}>
        {/* Header */}
        <div className={`px-4 py-3 flex items-center justify-between flex-shrink-0 ${isLightTheme ? 'border-b border-gray-300' : 'border-b border-gray-700/50'}`}>
          <h3 className={`text-sm font-semibold uppercase tracking-wide ${isLightTheme ? 'text-rose-600' : 'text-purple-400'}`}>Item Details</h3>
          <button
            onClick={onClose}
            className={`p-1 rounded transition-colors duration-150 ${isLightTheme ? 'hover:bg-gray-200' : 'hover:bg-gray-700'}`}
          >
            <svg className={`w-4 h-4 ${isLightTheme ? 'text-gray-600 hover:text-gray-800' : 'text-gray-400 hover:text-white'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-4 py-3 flex-1 overflow-y-auto">
          <MarkdownEditor
            content={item.content || item.description || ''}
            onSave={handleSave}
            onChange={setEditedContent}
            externalControls={true}
            isEditing={isEditing}
            onEdit={handleEdit}
            fontSize="text-sm"
            showBorder={true}
            textareaClassName="h-full"
            className="h-full"
          />
        </div>

        {/* Footer Buttons */}
        <div className={`px-4 py-3 flex gap-2 justify-end flex-shrink-0 ${isLightTheme ? 'border-t border-gray-300' : 'border-t border-gray-700/50'}`}>
          {!isEditing ? (
            <>
              <button
                onClick={handleClose}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors duration-150"
                style={{ color: 'var(--color-text-on-accent)' }}
              >
                Close
              </button>
              <button
                onClick={handleEdit}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors duration-150 flex items-center gap-2"
                style={{ color: 'var(--color-text-on-accent)' }}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleClose}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors duration-150"
                style={{ color: 'var(--color-text-on-accent)' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-800 disabled:cursor-not-allowed rounded-lg transition-colors duration-150 flex items-center gap-2"
                style={{ color: 'var(--color-text-on-accent)' }}
              >
                {isSaving ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Save
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
}
